import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchoolIcon from '@mui/icons-material/School';

const Services: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Define service categories with icons
  const serviceCategories = [
    {
      id: 'cmmi',
      title: 'CMMI Services',
      icon: <DocumentScannerIcon sx={{ fontSize: 50 }} />,
      color: '#1976d2',
      description: 'Our CMMI services help organizations improve their process capabilities through structured appraisals and maturity level assessments.',
      services: [
        { id: 'cmmi-appraisal', name: 'CMMI Capability Level-1 Assessment' },
        { id: 'cmmi-appraisal', name: 'CMMI Capability Level-2 Assessment' },
        { id: 'cmmi-appraisal', name: 'CMMI Capability Level-3 Assessment' },
        { id: 'cmmi-appraisal', name: 'CMMI Capability Level-4 Assessment' },
        { id: 'cmmi-appraisal', name: 'CMMI Capability Level-5 Assessment' },
        { id: 'cmmi-consultancy', name: 'CMMI Training' },
        { id: 'cmmi-consultancy', name: 'CMMI Consulting' }
      ]
    },
    {
      id: 'iso',
      title: 'ISO Services',
      icon: <SecurityIcon sx={{ fontSize: 50 }} />,
      color: '#2e7d32',
      description: 'Our ISO services provide comprehensive support for certification and compliance with international standards.',
      services: [
        { id: 'iso-9001', name: 'ISO 9001 - Quality Management System' },
        { id: 'iso-27001', name: 'ISO 27001 - Information Security Management System' },
        { id: 'iso-20000', name: 'ISO 20000-1 - Information Technology Service Management' },
        { id: 'iso-13485', name: 'ISO 13485 - Medical Devices – Quality Management Systems' },
        { id: 'iso-14001', name: 'ISO 14001 - Environmental Management Systems' }
      ]
    },
    {
      id: 'digital',
      title: 'Digital Services',
      icon: <DevicesIcon sx={{ fontSize: 50 }} />,
      color: '#7b1fa2',
      description: 'Our digital transformation services help businesses leverage the latest technologies for growth and efficiency.',
      services: [
        { id: 'digital-transformation', name: 'Digital Transformation Solutions' },
        { id: 'software-development', name: 'Software Development' },
        { id: 'desktop-development', name: 'Desktop Application Development' },
        { id: 'mobile-development', name: 'Mobile App Development' },
        { id: 'qa-services', name: 'Quality Assurance & Testing' }
      ]
    },
    {
      id: 'training',
      title: 'Training Services',
      icon: <SchoolIcon sx={{ fontSize: 50 }} />,
      color: '#9c27b0',
      description: 'Our professional training services help organizations build competencies and skills across various domains.',
      services: [
        { id: 'training', name: 'CMMI Professional Training' },
        { id: 'training', name: 'ISO Standards Training' },
        { id: 'training', name: 'Project Management Training' },
        { id: 'training', name: 'Quality Management Training' },
        { id: 'training', name: 'Custom Corporate Training Solutions' }
      ]
    },
    {
      id: 'other',
      title: 'Other Services',
      icon: <BuildIcon sx={{ fontSize: 50 }} />,
      color: '#e65100',
      description: 'We offer a range of additional services to help organizations achieve their strategic objectives.',
      services: [
        { id: 'data-entry', name: 'Data Entry Services' },
        { id: 'hr-outsourcing', name: 'HR Outsourcing Services' },
        { id: 'research', name: 'Research & Development' },
        { id: 'telecom', name: 'Managed Services for Telecom Sector' },
        { id: 'other', name: 'Quality Management System implementation' }
      ]
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              Our Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive solutions tailored to your needs
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Services Grid */}
      <Container maxWidth="lg" sx={{ pt: 10, pb: 15 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="services-grid"
        >
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              md: 'repeat(2, 1fr)' 
            }, 
            gap: 5 
          }}>
            {serviceCategories.map((category, index) => (
              <motion.div key={index} variants={itemVariants} id={category.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    borderRadius: '16px',
                    height: '100%',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                    }
                  }}
                >
                  {/* Category Icon */}
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: `${category.color}15`,
                      color: category.color,
                      mb: 3,
                      mx: isMobile ? 'auto' : 0
                    }}
                  >
                    {category.icon}
                  </Box>

                  {/* Category Title */}
                  <Typography 
                    variant="h4" 
                    component="h2" 
                    fontWeight="bold" 
                    gutterBottom
                    align={isMobile ? 'center' : 'left'}
                    sx={{ color: category.color }}
                  >
                    {category.title}
                  </Typography>

                  {/* Category Description */}
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph
                    align={isMobile ? 'center' : 'left'}
                    sx={{ mb: 4 }}
                  >
                    {category.description}
                  </Typography>

                  <Divider sx={{ my: 3 }} />

                  {/* Services List */}
                  <Box sx={{ mb: 4 }}>
                    {category.services.map((service, idx) => (
                      <Box 
                        key={idx} 
                        id={service.id}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 2,
                          gap: 2
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: category.color, flexShrink: 0 }} />
                        <Typography variant="body1">
                          {service.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Learn More Button */}
                  <Button
                    variant="outlined"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      borderColor: category.color,
                      color: category.color,
                      '&:hover': {
                        borderColor: category.color,
                        backgroundColor: `${category.color}10`,
                      },
                      borderRadius: '8px',
                      py: 1,
                      px: 3
                    }}
                    component="a"
                    href={`/contact?service=${category.id}`}
                  >
                    Learn More
                  </Button>

                  {/* Decorative corner */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 0,
                      height: 0,
                      borderStyle: 'solid',
                      borderWidth: '0 48px 48px 0',
                      borderColor: `transparent ${category.color} transparent transparent`,
                      opacity: 0.2
                    }}
                  />
                </Paper>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        backgroundColor: '#f5f7fa', 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Get Started?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our team of experts is ready to help you implement the right solutions for your business. 
                Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="/contact"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                  component="a"
                  href="/contact?request=brochure"
                >
                  Download Brochure
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Services; 